import {
  DefaultLoadingManager, LoaderUtils, FileLoader, Color, DirectionalLight, PointLight, SpotLight,
  MeshBasicMaterial, ShaderMaterial, ShaderLib, UniformsUtils, Interpolant,
  Matrix3, Matrix4, Vector2, Vector3, Vector4, Texture,
  NearestFilter, LinearFilter, NearestMipMapNearestFilter, LinearMipMapNearestFilter, NearestMipMapLinearFilter, LinearMipMapLinearFilter,
  ClampToEdgeWrapping, MirroredRepeatWrapping, RepeatWrapping,
  BackSide, FrontSide, NeverDepth, LessDepth, EqualDepth, LessEqualDepth, GreaterEqualDepth, NotEqualDepth, AlwaysDepth, AddEquation, SubtractEquation, ReverseSubtractEquation,
  ZeroFactor, OneFactor, SrcColorFactor, OneMinusSrcColorFactor, SrcAlphaFactor, OneMinusSrcAlphaFactor, DstAlphaFactor, OneMinusDstAlphaFactor, DstColorFactor, OneMinusDstColorFactor, SrcAlphaSaturateFactor,
  InterpolateLinear, InterpolateDiscrete, RGBAFormat, RGBFormat, MeshStandardMaterial, BufferAttribute, TextureLoader, InterleavedBuffer, InterleavedBufferAttribute, Loader, PointsMaterial, Material, LineBasicMaterial,
  VertexColors, sRGBEncoding, BufferGeometry, SkinnedMesh, Mesh, TriangleStripDrawMode, TriangleFanDrawMode, LineSegments, Line, LineLoop, Points, Group, PerspectiveCamera, OrthographicCamera, NumberKeyframeTrack, QuaternionKeyframeTrack, VectorKeyframeTrack,
  AnimationClip, Bone, Object3D, PropertyBinding, Skeleton, Scene,
} from 'three';


/**
 * @author Rich Tibbett / https://github.com/richtr
 * @author mrdoob / http://mrdoob.com/
 * @author Tony Parisi / http://www.tonyparisi.com/
 * @author Takahiro / https://github.com/takahirox
 * @author Don McCurdy / https://www.donmccurdy.com
 */

function GLTFLoader(manager) {
  this.manager = (manager !== undefined) ? manager : DefaultLoadingManager;
  this.dracoLoader = null;
}

GLTFLoader.prototype = {

  constructor: GLTFLoader,

  crossOrigin: 'anonymous',

  load(url, onLoad, onProgress, onError) {
    const scope = this;

    let resourcePath;

    if (this.resourcePath !== undefined) {
      resourcePath = this.resourcePath;
    } else if (this.path !== undefined) {
      resourcePath = this.path;
    } else {
      resourcePath = LoaderUtils.extractUrlBase(url);
    }

    // Tells the LoadingManager to track an extra item, which resolves after
    // the model is fully loaded. This means the count of items loaded will
    // be incorrect, but ensures manager.onLoad() does not fire early.
    scope.manager.itemStart(url);

    const _onError = function (e) {
      if (onError) {
        onError(e);
      } else {
        console.error(e);
      }

      scope.manager.itemError(url);
      scope.manager.itemEnd(url);
    };

    const loader = new FileLoader(scope.manager);

    loader.setPath(this.path);
    loader.setResponseType('arraybuffer');

    loader.load(url, (data) => {
      try {
        scope.parse(data, resourcePath, (gltf) => {
          onLoad(gltf);

          scope.manager.itemEnd(url);
        }, _onError);
      } catch (e) {
        _onError(e);
      }
    }, onProgress, _onError);
  },

  setCrossOrigin(value) {
    this.crossOrigin = value;
    return this;
  },

  setPath(value) {
    this.path = value;
    return this;
  },

  setResourcePath(value) {
    this.resourcePath = value;
    return this;
  },

  setDRACOLoader(dracoLoader) {
    this.dracoLoader = dracoLoader;
    return this;
  },

  parse(data, path, onLoad, onError) {
    let content;
    const extensions = {};

    if (typeof data === 'string') {
      content = data;
    } else {
      const magic = LoaderUtils.decodeText(new Uint8Array(data, 0, 4));

      if (magic === BINARY_EXTENSION_HEADER_MAGIC) {
        try {
          extensions[EXTENSIONS.KHR_BINARY_GLTF] = new GLTFBinaryExtension(data);
        } catch (error) {
          if (onError) onError(error);
          return;
        }

        content = extensions[EXTENSIONS.KHR_BINARY_GLTF].content;
      } else {
        content = LoaderUtils.decodeText(new Uint8Array(data));
      }
    }

    const json = JSON.parse(content);

    if (json.asset === undefined || json.asset.version[0] < 2) {
      if (onError) onError(new Error('GLTFLoader: Unsupported asset. glTF versions >=2.0 are supported. Use LegacyGLTFLoader instead.'));
      return;
    }

    if (json.extensionsUsed) {
      for (let i = 0; i < json.extensionsUsed.length; ++i) {
        const extensionName = json.extensionsUsed[i];
        const extensionsRequired = json.extensionsRequired || [];

        switch (extensionName) {
        case EXTENSIONS.KHR_LIGHTS_PUNCTUAL:
          extensions[extensionName] = new GLTFLightsExtension(json);
          break;

        case EXTENSIONS.KHR_MATERIALS_UNLIT:
          extensions[extensionName] = new GLTFMaterialsUnlitExtension(json);
          break;

        case EXTENSIONS.KHR_MATERIALS_PBR_SPECULAR_GLOSSINESS:
          extensions[extensionName] = new GLTFMaterialsPbrSpecularGlossinessExtension(json);
          break;

        case EXTENSIONS.KHR_DRACO_MESH_COMPRESSION:
          extensions[extensionName] = new GLTFDracoMeshCompressionExtension(json, this.dracoLoader);
          break;

        case EXTENSIONS.MSFT_TEXTURE_DDS:
          extensions[EXTENSIONS.MSFT_TEXTURE_DDS] = new GLTFTextureDDSExtension();
          break;

        case EXTENSIONS.KHR_TEXTURE_TRANSFORM:
          extensions[EXTENSIONS.KHR_TEXTURE_TRANSFORM] = new GLTFTextureTransformExtension(json);
          break;

        default:

          if (extensionsRequired.indexOf(extensionName) >= 0) {
            console.warn(`GLTFLoader: Unknown extension "${extensionName}".`);
          }
        }
      }
    }

    const parser = new GLTFParser(json, extensions, {

      path: path || this.resourcePath || '',
      crossOrigin: this.crossOrigin,
      manager: this.manager,

    });

    parser.parse(onLoad, onError);
  },

};

/* GLTFREGISTRY */

function GLTFRegistry() {
  let objects = {};

  return	{

    get(key) {
      return objects[key];
    },

    add(key, object) {
      objects[key] = object;
    },

    remove(key) {
      delete objects[key];
    },

    removeAll() {
      objects = {};
    },

  };
}

/** ****************************** */
/** ******** EXTENSIONS ********** */
/** ****************************** */

var EXTENSIONS = {
  KHR_BINARY_GLTF: 'KHR_binary_glTF',
  KHR_DRACO_MESH_COMPRESSION: 'KHR_draco_mesh_compression',
  KHR_LIGHTS_PUNCTUAL: 'KHR_lights_punctual',
  KHR_MATERIALS_PBR_SPECULAR_GLOSSINESS: 'KHR_materials_pbrSpecularGlossiness',
  KHR_MATERIALS_UNLIT: 'KHR_materials_unlit',
  KHR_TEXTURE_TRANSFORM: 'KHR_texture_transform',
  MSFT_TEXTURE_DDS: 'MSFT_texture_dds',
};

/**
 * DDS Texture Extension
 *
 * Specification:
 * https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Vendor/MSFT_texture_dds
 *
 */
function GLTFTextureDDSExtension() {
  // if ( ! DDSLoader ) {
  //
  //     //throw new Error( 'GLTFLoader: Attempting to load .dds texture without importing DDSLoader' );
  //
  // }

  this.name = EXTENSIONS.MSFT_TEXTURE_DDS;
  // this.ddsLoader = new DDSLoader();
}

/**
 * Lights Extension
 *
 * Specification: PENDING
 */
function GLTFLightsExtension(json) {
  this.name = EXTENSIONS.KHR_LIGHTS_PUNCTUAL;

  const extension = (json.extensions && json.extensions[EXTENSIONS.KHR_LIGHTS_PUNCTUAL]) || {};
  this.lightDefs = extension.lights || [];
}

GLTFLightsExtension.prototype.loadLight = function (lightIndex) {
  const lightDef = this.lightDefs[lightIndex];
  let lightNode;

  const color = new Color(0xffffff);
  if (lightDef.color !== undefined) color.fromArray(lightDef.color);

  const range = lightDef.range !== undefined ? lightDef.range : 0;

  switch (lightDef.type) {
  case 'directional':
    lightNode = new DirectionalLight(color);
    lightNode.target.position.set(0, 0, -1);
    lightNode.add(lightNode.target);
    break;

  case 'point':
    lightNode = new PointLight(color);
    lightNode.distance = range;
    break;

  case 'spot':
    lightNode = new SpotLight(color);
    lightNode.distance = range;
    // Handle spotlight properties.
    lightDef.spot = lightDef.spot || {};
    lightDef.spot.innerConeAngle = lightDef.spot.innerConeAngle !== undefined ? lightDef.spot.innerConeAngle : 0;
    lightDef.spot.outerConeAngle = lightDef.spot.outerConeAngle !== undefined ? lightDef.spot.outerConeAngle : Math.PI / 4.0;
    lightNode.angle = lightDef.spot.outerConeAngle;
    lightNode.penumbra = 1.0 - lightDef.spot.innerConeAngle / lightDef.spot.outerConeAngle;
    lightNode.target.position.set(0, 0, -1);
    lightNode.add(lightNode.target);
    break;

  default:
    throw new Error(`GLTFLoader: Unexpected light type, "${lightDef.type}".`);
  }

  // Some lights (e.g. spot) default to a position other than the origin. Reset the position
  // here, because node-level parsing will only override position if explicitly specified.
  lightNode.position.set(0, 0, 0);

  lightNode.decay = 2;

  if (lightDef.intensity !== undefined) lightNode.intensity = lightDef.intensity;

  lightNode.name = lightDef.name || (`light_${lightIndex}`);

  return Promise.resolve(lightNode);
};

/**
 * Unlit Materials Extension (pending)
 *
 * PR: https://github.com/KhronosGroup/glTF/pull/1163
 */
function GLTFMaterialsUnlitExtension(json) {
  this.name = EXTENSIONS.KHR_MATERIALS_UNLIT;
}

GLTFMaterialsUnlitExtension.prototype.getMaterialType = function (material) {
  return MeshBasicMaterial;
};

GLTFMaterialsUnlitExtension.prototype.extendParams = function (materialParams, material, parser) {
  const pending = [];

  materialParams.color = new Color(1.0, 1.0, 1.0);
  materialParams.opacity = 1.0;

  const metallicRoughness = material.pbrMetallicRoughness;

  if (metallicRoughness) {
    if (Array.isArray(metallicRoughness.baseColorFactor)) {
      const array = metallicRoughness.baseColorFactor;

      materialParams.color.fromArray(array);
      materialParams.opacity = array[3];
    }

    if (metallicRoughness.baseColorTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'map', metallicRoughness.baseColorTexture));
    }
  }

  return Promise.all(pending);
};

/* BINARY EXTENSION */

const BINARY_EXTENSION_BUFFER_NAME = 'binary_glTF';
var BINARY_EXTENSION_HEADER_MAGIC = 'glTF';
const BINARY_EXTENSION_HEADER_LENGTH = 12;
const BINARY_EXTENSION_CHUNK_TYPES = { JSON: 0x4E4F534A, BIN: 0x004E4942 };

function GLTFBinaryExtension(data) {
  this.name = EXTENSIONS.KHR_BINARY_GLTF;
  this.content = null;
  this.body = null;

  const headerView = new DataView(data, 0, BINARY_EXTENSION_HEADER_LENGTH);

  this.header = {
    magic: LoaderUtils.decodeText(new Uint8Array(data.slice(0, 4))),
    version: headerView.getUint32(4, true),
    length: headerView.getUint32(8, true),
  };

  if (this.header.magic !== BINARY_EXTENSION_HEADER_MAGIC) {
    throw new Error('GLTFLoader: Unsupported glTF-Binary header.');
  } else if (this.header.version < 2.0) {
    throw new Error('GLTFLoader: Legacy binary file detected. Use LegacyGLTFLoader instead.');
  }

  const chunkView = new DataView(data, BINARY_EXTENSION_HEADER_LENGTH);
  let chunkIndex = 0;

  while (chunkIndex < chunkView.byteLength) {
    const chunkLength = chunkView.getUint32(chunkIndex, true);
    chunkIndex += 4;

    const chunkType = chunkView.getUint32(chunkIndex, true);
    chunkIndex += 4;

    if (chunkType === BINARY_EXTENSION_CHUNK_TYPES.JSON) {
      const contentArray = new Uint8Array(data, BINARY_EXTENSION_HEADER_LENGTH + chunkIndex, chunkLength);
      this.content = LoaderUtils.decodeText(contentArray);
    } else if (chunkType === BINARY_EXTENSION_CHUNK_TYPES.BIN) {
      const byteOffset = BINARY_EXTENSION_HEADER_LENGTH + chunkIndex;
      this.body = data.slice(byteOffset, byteOffset + chunkLength);
    }

    // Clients must ignore chunks with unknown types.

    chunkIndex += chunkLength;
  }

  if (this.content === null) {
    throw new Error('GLTFLoader: JSON content not found.');
  }
}

/**
 * DRACO Mesh Compression Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/pull/874
 */
function GLTFDracoMeshCompressionExtension(json, dracoLoader) {
  if (!dracoLoader) {
    throw new Error('GLTFLoader: No DRACOLoader instance provided.');
  }

  this.name = EXTENSIONS.KHR_DRACO_MESH_COMPRESSION;
  this.json = json;
  this.dracoLoader = dracoLoader;
}

GLTFDracoMeshCompressionExtension.prototype.decodePrimitive = function (primitive, parser) {
  const { json } = this;
  const { dracoLoader } = this;
  const bufferViewIndex = primitive.extensions[this.name].bufferView;
  const gltfAttributeMap = primitive.extensions[this.name].attributes;
  const threeAttributeMap = {};
  const attributeNormalizedMap = {};
  const attributeTypeMap = {};

  for (var attributeName in gltfAttributeMap) {
    var threeAttributeName = ATTRIBUTES[attributeName] || attributeName.toLowerCase();

    threeAttributeMap[threeAttributeName] = gltfAttributeMap[attributeName];
  }

  for (attributeName in primitive.attributes) {
    var threeAttributeName = ATTRIBUTES[attributeName] || attributeName.toLowerCase();

    if (gltfAttributeMap[attributeName] !== undefined) {
      const accessorDef = json.accessors[primitive.attributes[attributeName]];
      const componentType = WEBGL_COMPONENT_TYPES[accessorDef.componentType];

      attributeTypeMap[threeAttributeName] = componentType;
      attributeNormalizedMap[threeAttributeName] = accessorDef.normalized === true;
    }
  }

  return parser.getDependency('bufferView', bufferViewIndex).then(bufferView => new Promise(((resolve) => {
    dracoLoader.decodeDracoFile(bufferView, (geometry) => {
      for (const attributeName in geometry.attributes) {
        const attribute = geometry.attributes[attributeName];
        const normalized = attributeNormalizedMap[attributeName];

        if (normalized !== undefined) attribute.normalized = normalized;
      }

      resolve(geometry);
    }, threeAttributeMap, attributeTypeMap);
  })));
};

/**
 * Texture Transform Extension
 *
 * Specification:
 */
function GLTFTextureTransformExtension(json) {
  this.name = EXTENSIONS.KHR_TEXTURE_TRANSFORM;
}

GLTFTextureTransformExtension.prototype.extendTexture = function (texture, transform) {
  texture = texture.clone();

  if (transform.offset !== undefined) {
    texture.offset.fromArray(transform.offset);
  }

  if (transform.rotation !== undefined) {
    texture.rotation = transform.rotation;
  }

  if (transform.scale !== undefined) {
    texture.repeat.fromArray(transform.scale);
  }

  if (transform.texCoord !== undefined) {
    console.warn(`GLTFLoader: Custom UV sets in "${this.name}" extension not yet supported.`);
  }

  texture.needsUpdate = true;

  return texture;
};

/**
 * Specular-Glossiness Extension
 *
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/extensions/2.0/Khronos/KHR_materials_pbrSpecularGlossiness
 */
function GLTFMaterialsPbrSpecularGlossinessExtension() {
  return {

    name: EXTENSIONS.KHR_MATERIALS_PBR_SPECULAR_GLOSSINESS,

    specularGlossinessParams: [
      'color',
      'map',
      'lightMap',
      'lightMapIntensity',
      'aoMap',
      'aoMapIntensity',
      'emissive',
      'emissiveIntensity',
      'emissiveMap',
      'bumpMap',
      'bumpScale',
      'normalMap',
      'displacementMap',
      'displacementScale',
      'displacementBias',
      'specularMap',
      'specular',
      'glossinessMap',
      'glossiness',
      'alphaMap',
      'envMap',
      'envMapIntensity',
      'refractionRatio',
    ],

    getMaterialType() {
      return ShaderMaterial;
    },

    extendParams(params, material, parser) {
      const pbrSpecularGlossiness = material.extensions[this.name];

      const shader = ShaderLib.standard;

      const uniforms = UniformsUtils.clone(shader.uniforms);

      const specularMapParsFragmentChunk = [
        '#ifdef USE_SPECULARMAP',
        '	uniform sampler2D specularMap;',
        '#endif',
      ].join('\n');

      const glossinessMapParsFragmentChunk = [
        '#ifdef USE_GLOSSINESSMAP',
        '	uniform sampler2D glossinessMap;',
        '#endif',
      ].join('\n');

      const specularMapFragmentChunk = [
        'vec3 specularFactor = specular;',
        '#ifdef USE_SPECULARMAP',
        '	vec4 texelSpecular = texture2D( specularMap, vUv );',
        '	texelSpecular = sRGBToLinear( texelSpecular );',
        '	// reads channel RGB, compatible with a glTF Specular-Glossiness (RGBA) texture',
        '	specularFactor *= texelSpecular.rgb;',
        '#endif',
      ].join('\n');

      const glossinessMapFragmentChunk = [
        'float glossinessFactor = glossiness;',
        '#ifdef USE_GLOSSINESSMAP',
        '	vec4 texelGlossiness = texture2D( glossinessMap, vUv );',
        '	// reads channel A, compatible with a glTF Specular-Glossiness (RGBA) texture',
        '	glossinessFactor *= texelGlossiness.a;',
        '#endif',
      ].join('\n');

      const lightPhysicalFragmentChunk = [
        'PhysicalMaterial material;',
        'material.diffuseColor = diffuseColor.rgb;',
        'material.specularRoughness = clamp( 1.0 - glossinessFactor, 0.04, 1.0 );',
        'material.specularColor = specularFactor.rgb;',
      ].join('\n');

      const fragmentShader = shader.fragmentShader
        .replace('uniform float roughness;', 'uniform vec3 specular;')
        .replace('uniform float metalness;', 'uniform float glossiness;')
        .replace('#include <roughnessmap_pars_fragment>', specularMapParsFragmentChunk)
        .replace('#include <metalnessmap_pars_fragment>', glossinessMapParsFragmentChunk)
        .replace('#include <roughnessmap_fragment>', specularMapFragmentChunk)
        .replace('#include <metalnessmap_fragment>', glossinessMapFragmentChunk)
        .replace('#include <lights_physical_fragment>', lightPhysicalFragmentChunk);

      delete uniforms.roughness;
      delete uniforms.metalness;
      delete uniforms.roughnessMap;
      delete uniforms.metalnessMap;

      uniforms.specular = { value: new Color().setHex(0x111111) };
      uniforms.glossiness = { value: 0.5 };
      uniforms.specularMap = { value: null };
      uniforms.glossinessMap = { value: null };

      params.vertexShader = shader.vertexShader;
      params.fragmentShader = fragmentShader;
      params.uniforms = uniforms;
      params.defines = { STANDARD: '' };

      params.color = new Color(1.0, 1.0, 1.0);
      params.opacity = 1.0;

      const pending = [];

      if (Array.isArray(pbrSpecularGlossiness.diffuseFactor)) {
        const array = pbrSpecularGlossiness.diffuseFactor;

        params.color.fromArray(array);
        params.opacity = array[3];
      }

      if (pbrSpecularGlossiness.diffuseTexture !== undefined) {
        pending.push(parser.assignTexture(params, 'map', pbrSpecularGlossiness.diffuseTexture));
      }

      params.emissive = new Color(0.0, 0.0, 0.0);
      params.glossiness = pbrSpecularGlossiness.glossinessFactor !== undefined ? pbrSpecularGlossiness.glossinessFactor : 1.0;
      params.specular = new Color(1.0, 1.0, 1.0);

      if (Array.isArray(pbrSpecularGlossiness.specularFactor)) {
        params.specular.fromArray(pbrSpecularGlossiness.specularFactor);
      }

      if (pbrSpecularGlossiness.specularGlossinessTexture !== undefined) {
        const specGlossMapDef = pbrSpecularGlossiness.specularGlossinessTexture;
        pending.push(parser.assignTexture(params, 'glossinessMap', specGlossMapDef));
        pending.push(parser.assignTexture(params, 'specularMap', specGlossMapDef));
      }

      return Promise.all(pending);
    },

    createMaterial(params) {
      // setup material properties based on MeshStandardMaterial for Specular-Glossiness

      const material = new ShaderMaterial({
        defines: params.defines,
        vertexShader: params.vertexShader,
        fragmentShader: params.fragmentShader,
        uniforms: params.uniforms,
        fog: true,
        lights: true,
        opacity: params.opacity,
        transparent: params.transparent,
      });

      material.isGLTFSpecularGlossinessMaterial = true;

      material.color = params.color;

      material.map = params.map === undefined ? null : params.map;

      material.lightMap = null;
      material.lightMapIntensity = 1.0;

      material.aoMap = params.aoMap === undefined ? null : params.aoMap;
      material.aoMapIntensity = 1.0;

      material.emissive = params.emissive;
      material.emissiveIntensity = 1.0;
      material.emissiveMap = params.emissiveMap === undefined ? null : params.emissiveMap;

      material.bumpMap = params.bumpMap === undefined ? null : params.bumpMap;
      material.bumpScale = 1;

      material.normalMap = params.normalMap === undefined ? null : params.normalMap;
      if (params.normalScale) material.normalScale = params.normalScale;

      material.displacementMap = null;
      material.displacementScale = 1;
      material.displacementBias = 0;

      material.specularMap = params.specularMap === undefined ? null : params.specularMap;
      material.specular = params.specular;

      material.glossinessMap = params.glossinessMap === undefined ? null : params.glossinessMap;
      material.glossiness = params.glossiness;

      material.alphaMap = null;

      material.envMap = params.envMap === undefined ? null : params.envMap;
      material.envMapIntensity = 1.0;

      material.refractionRatio = 0.98;

      material.extensions.derivatives = true;

      return material;
    },

    /**
         * Clones a GLTFSpecularGlossinessMaterial instance. The ShaderMaterial.copy() method can
         * copy only properties it knows about or inherits, and misses many properties that would
         * normally be defined by MeshStandardMaterial.
         *
         * This method allows GLTFSpecularGlossinessMaterials to be cloned in the process of
         * loading a glTF model, but cloning later (e.g. by the user) would require these changes
         * AND also updating `.onBeforeRender` on the parent mesh.
         *
         * @param  {ShaderMaterial} source
         * @return {ShaderMaterial}
         */
    cloneMaterial(source) {
      const target = source.clone();

      target.isGLTFSpecularGlossinessMaterial = true;

      const params = this.specularGlossinessParams;

      for (let i = 0, il = params.length; i < il; i++) {
        target[params[i]] = source[params[i]];
      }

      return target;
    },

    // Here's based on refreshUniformsCommon() and refreshUniformsStandard() in WebGLRenderer.
    refreshUniforms(renderer, scene, camera, geometry, material, group) {
      if (material.isGLTFSpecularGlossinessMaterial !== true) {
        return;
      }

      const { uniforms } = material;
      const { defines } = material;

      uniforms.opacity.value = material.opacity;

      uniforms.diffuse.value.copy(material.color);
      uniforms.emissive.value.copy(material.emissive).multiplyScalar(material.emissiveIntensity);

      uniforms.map.value = material.map;
      uniforms.specularMap.value = material.specularMap;
      uniforms.alphaMap.value = material.alphaMap;

      uniforms.lightMap.value = material.lightMap;
      uniforms.lightMapIntensity.value = material.lightMapIntensity;

      uniforms.aoMap.value = material.aoMap;
      uniforms.aoMapIntensity.value = material.aoMapIntensity;

      // uv repeat and offset setting priorities
      // 1. color map
      // 2. specular map
      // 3. normal map
      // 4. bump map
      // 5. alpha map
      // 6. emissive map

      let uvScaleMap;

      if (material.map) {
        uvScaleMap = material.map;
      } else if (material.specularMap) {
        uvScaleMap = material.specularMap;
      } else if (material.displacementMap) {
        uvScaleMap = material.displacementMap;
      } else if (material.normalMap) {
        uvScaleMap = material.normalMap;
      } else if (material.bumpMap) {
        uvScaleMap = material.bumpMap;
      } else if (material.glossinessMap) {
        uvScaleMap = material.glossinessMap;
      } else if (material.alphaMap) {
        uvScaleMap = material.alphaMap;
      } else if (material.emissiveMap) {
        uvScaleMap = material.emissiveMap;
      }

      if (uvScaleMap !== undefined) {
        // backwards compatibility
        if (uvScaleMap.isWebGLRenderTarget) {
          uvScaleMap = uvScaleMap.texture;
        }

        if (uvScaleMap.matrixAutoUpdate === true) {
          uvScaleMap.updateMatrix();
        }

        uniforms.uvTransform.value.copy(uvScaleMap.matrix);
      }

      if (material.envMap) {
        uniforms.envMap.value = material.envMap;
        uniforms.envMapIntensity.value = material.envMapIntensity;

        // don't flip CubeTexture envMaps, flip everything else:
        //  WebGLRenderTargetCube will be flipped for backwards compatibility
        //  WebGLRenderTargetCube.texture will be flipped because it's a Texture and NOT a CubeTexture
        // this check must be handled differently, or removed entirely, if WebGLRenderTargetCube uses a CubeTexture in the future
        uniforms.flipEnvMap.value = material.envMap.isCubeTexture ? -1 : 1;

        uniforms.reflectivity.value = material.reflectivity;
        uniforms.refractionRatio.value = material.refractionRatio;

        uniforms.maxMipLevel.value = renderer.properties.get(material.envMap).__maxMipLevel;
      }

      uniforms.specular.value.copy(material.specular);
      uniforms.glossiness.value = material.glossiness;

      uniforms.glossinessMap.value = material.glossinessMap;

      uniforms.emissiveMap.value = material.emissiveMap;
      uniforms.bumpMap.value = material.bumpMap;
      uniforms.normalMap.value = material.normalMap;

      uniforms.displacementMap.value = material.displacementMap;
      uniforms.displacementScale.value = material.displacementScale;
      uniforms.displacementBias.value = material.displacementBias;

      if (uniforms.glossinessMap.value !== null && defines.USE_GLOSSINESSMAP === undefined) {
        defines.USE_GLOSSINESSMAP = '';
        // set USE_ROUGHNESSMAP to enable vUv
        defines.USE_ROUGHNESSMAP = '';
      }

      if (uniforms.glossinessMap.value === null && defines.USE_GLOSSINESSMAP !== undefined) {
        delete defines.USE_GLOSSINESSMAP;
        delete defines.USE_ROUGHNESSMAP;
      }
    },

  };
}

/** ****************************** */
/** ******** INTERPOLATION ******* */
/** ****************************** */

// Spline Interpolation
// Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#appendix-c-spline-interpolation
function GLTFCubicSplineInterpolant(parameterPositions, sampleValues, sampleSize, resultBuffer) {
  Interpolant.call(this, parameterPositions, sampleValues, sampleSize, resultBuffer);
}

GLTFCubicSplineInterpolant.prototype = Object.create(Interpolant.prototype);
GLTFCubicSplineInterpolant.prototype.constructor = GLTFCubicSplineInterpolant;

GLTFCubicSplineInterpolant.prototype.copySampleValue_ = function (index) {
  // Copies a sample value to the result buffer. See description of glTF
  // CUBICSPLINE values layout in interpolate_() function below.

  const result = this.resultBuffer;
  const values = this.sampleValues;
  const { valueSize } = this;
  const offset = index * valueSize * 3 + valueSize;

  for (let i = 0; i !== valueSize; i++) {
    result[i] = values[offset + i];
  }

  return result;
};

GLTFCubicSplineInterpolant.prototype.beforeStart_ = GLTFCubicSplineInterpolant.prototype.copySampleValue_;

GLTFCubicSplineInterpolant.prototype.afterEnd_ = GLTFCubicSplineInterpolant.prototype.copySampleValue_;

GLTFCubicSplineInterpolant.prototype.interpolate_ = function (i1, t0, t, t1) {
  const result = this.resultBuffer;
  const values = this.sampleValues;
  const stride = this.valueSize;

  const stride2 = stride * 2;
  const stride3 = stride * 3;

  const td = t1 - t0;

  const p = (t - t0) / td;
  const pp = p * p;
  const ppp = pp * p;

  const offset1 = i1 * stride3;
  const offset0 = offset1 - stride3;

  const s2 = -2 * ppp + 3 * pp;
  const s3 = ppp - pp;
  const s0 = 1 - s2;
  const s1 = s3 - pp + p;

  // Layout of keyframe output values for CUBICSPLINE animations:
  //   [ inTangent_1, splineVertex_1, outTangent_1, inTangent_2, splineVertex_2, ... ]
  for (let i = 0; i !== stride; i++) {
    const p0 = values[offset0 + i + stride]; // splineVertex_k
    const m0 = values[offset0 + i + stride2] * td; // outTangent_k * (t_k+1 - t_k)
    const p1 = values[offset1 + i + stride]; // splineVertex_k+1
    const m1 = values[offset1 + i] * td; // inTangent_k+1 * (t_k+1 - t_k)

    result[i] = s0 * p0 + s1 * m0 + s2 * p1 + s3 * m1;
  }

  return result;
};

/** ****************************** */
/** ******** INTERNALS *********** */
/** ****************************** */

/* CONSTANTS */

const WEBGL_CONSTANTS = {
  FLOAT: 5126,
  // FLOAT_MAT2: 35674,
  FLOAT_MAT3: 35675,
  FLOAT_MAT4: 35676,
  FLOAT_VEC2: 35664,
  FLOAT_VEC3: 35665,
  FLOAT_VEC4: 35666,
  LINEAR: 9729,
  REPEAT: 10497,
  SAMPLER_2D: 35678,
  POINTS: 0,
  LINES: 1,
  LINE_LOOP: 2,
  LINE_STRIP: 3,
  TRIANGLES: 4,
  TRIANGLE_STRIP: 5,
  TRIANGLE_FAN: 6,
  UNSIGNED_BYTE: 5121,
  UNSIGNED_SHORT: 5123,
};

const WEBGL_TYPE = {
  5126: Number,
  // 35674: Matrix2,
  35675: Matrix3,
  35676: Matrix4,
  35664: Vector2,
  35665: Vector3,
  35666: Vector4,
  35678: Texture,
};

var WEBGL_COMPONENT_TYPES = {
  5120: Int8Array,
  5121: Uint8Array,
  5122: Int16Array,
  5123: Uint16Array,
  5125: Uint32Array,
  5126: Float32Array,
};

const WEBGL_FILTERS = {
  9728: NearestFilter,
  9729: LinearFilter,
  9984: NearestMipMapNearestFilter,
  9985: LinearMipMapNearestFilter,
  9986: NearestMipMapLinearFilter,
  9987: LinearMipMapLinearFilter,
};

const WEBGL_WRAPPINGS = {
  33071: ClampToEdgeWrapping,
  33648: MirroredRepeatWrapping,
  10497: RepeatWrapping,
};

const WEBGL_SIDES = {
  1028: BackSide, // Culling front
  1029: FrontSide, // Culling back
  // 1032: NoSide   // Culling front and back, what to do?
};

const WEBGL_DEPTH_FUNCS = {
  512: NeverDepth,
  513: LessDepth,
  514: EqualDepth,
  515: LessEqualDepth,
  516: GreaterEqualDepth,
  517: NotEqualDepth,
  518: GreaterEqualDepth,
  519: AlwaysDepth,
};

const WEBGL_BLEND_EQUATIONS = {
  32774: AddEquation,
  32778: SubtractEquation,
  32779: ReverseSubtractEquation,
};

const WEBGL_BLEND_FUNCS = {
  0: ZeroFactor,
  1: OneFactor,
  768: SrcColorFactor,
  769: OneMinusSrcColorFactor,
  770: SrcAlphaFactor,
  771: OneMinusSrcAlphaFactor,
  772: DstAlphaFactor,
  773: OneMinusDstAlphaFactor,
  774: DstColorFactor,
  775: OneMinusDstColorFactor,
  776: SrcAlphaSaturateFactor,
  // The followings are not supported by Three.js yet
  // 32769: CONSTANT_COLOR,
  // 32770: ONE_MINUS_CONSTANT_COLOR,
  // 32771: CONSTANT_ALPHA,
  // 32772: ONE_MINUS_CONSTANT_COLOR
};

const WEBGL_TYPE_SIZES = {
  SCALAR: 1,
  VEC2: 2,
  VEC3: 3,
  VEC4: 4,
  MAT2: 4,
  MAT3: 9,
  MAT4: 16,
};

var ATTRIBUTES = {
  POSITION: 'position',
  NORMAL: 'normal',
  TANGENT: 'tangent',
  TEXCOORD_0: 'uv',
  TEXCOORD_1: 'uv2',
  COLOR_0: 'color',
  WEIGHTS_0: 'skinWeight',
  JOINTS_0: 'skinIndex',
};

const PATH_PROPERTIES = {
  scale: 'scale',
  translation: 'position',
  rotation: 'quaternion',
  weights: 'morphTargetInfluences',
};

const INTERPOLATION = {
  CUBICSPLINE: undefined, // We use a custom interpolant (GLTFCubicSplineInterpolation) for CUBICSPLINE tracks. Each
  // keyframe track will be initialized with a default interpolation type, then modified.
  LINEAR: InterpolateLinear,
  STEP: InterpolateDiscrete,
};

const STATES_ENABLES = {
  2884: 'CULL_FACE',
  2929: 'DEPTH_TEST',
  3042: 'BLEND',
  3089: 'SCISSOR_TEST',
  32823: 'POLYGON_OFFSET_FILL',
  32926: 'SAMPLE_ALPHA_TO_COVERAGE',
};

const ALPHA_MODES = {
  OPAQUE: 'OPAQUE',
  MASK: 'MASK',
  BLEND: 'BLEND',
};

const MIME_TYPE_FORMATS = {
  'image/png': RGBAFormat,
  'image/jpeg': RGBFormat,
};

/* UTILITY FUNCTIONS */

function resolveURL(url, path) {
  // Invalid URL
  if (typeof url !== 'string' || url === '') return '';

  // Absolute URL http://,https://,//
  if (/^(https?:)?\/\//i.test(url)) return url;

  // Data URI
  if (/^data:.*,.*$/i.test(url)) return url;

  // Blob URL
  if (/^blob:.*$/i.test(url)) return url;

  // Relative URL
  return path + url;
}

let defaultMaterial;

/**
 * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#default-material
 */
function createDefaultMaterial() {
  defaultMaterial = defaultMaterial || new MeshStandardMaterial({
    color: 0xFFFFFF,
    emissive: 0x000000,
    metalness: 1,
    roughness: 1,
    transparent: false,
    depthTest: true,
    side: FrontSide,
  });

  return defaultMaterial;
}

function addUnknownExtensionsToUserData(knownExtensions, object, objectDef) {
  // Add unknown glTF extensions to an object's userData.

  for (const name in objectDef.extensions) {
    if (knownExtensions[name] === undefined) {
      object.userData.gltfExtensions = object.userData.gltfExtensions || {};
      object.userData.gltfExtensions[name] = objectDef.extensions[name];
    }
  }
}

/**
 * @param {Object3D|Material|BufferGeometry} object
 * @param {GLTF.definition} gltfDef
 */
function assignExtrasToUserData(object, gltfDef) {
  if (gltfDef.extras !== undefined) {
    if (typeof gltfDef.extras === 'object') {
      object.userData = gltfDef.extras;
    } else {
      console.warn(`GLTFLoader: Ignoring primitive type .extras, ${gltfDef.extras}`);
    }
  }
}

/**
 * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#morph-targets
 *
 * @param {BufferGeometry} geometry
 * @param {Array<GLTF.Target>} targets
 * @param {GLTFParser} parser
 * @return {Promise<BufferGeometry>}
 */
function addMorphTargets(geometry, targets, parser) {
  let hasMorphPosition = false;
  let hasMorphNormal = false;

  for (var i = 0, il = targets.length; i < il; i++) {
    var target = targets[i];

    if (target.POSITION !== undefined) hasMorphPosition = true;
    if (target.NORMAL !== undefined) hasMorphNormal = true;

    if (hasMorphPosition && hasMorphNormal) break;
  }

  if (!hasMorphPosition && !hasMorphNormal) return Promise.resolve(geometry);

  const pendingPositionAccessors = [];
  const pendingNormalAccessors = [];

  for (var i = 0, il = targets.length; i < il; i++) {
    var target = targets[i];

    if (hasMorphPosition) {
      var pendingAccessor = target.POSITION !== undefined
        ? parser.getDependency('accessor', target.POSITION)
        : geometry.attributes.position;

      pendingPositionAccessors.push(pendingAccessor);
    }

    if (hasMorphNormal) {
      var pendingAccessor = target.NORMAL !== undefined
        ? parser.getDependency('accessor', target.NORMAL)
        : geometry.attributes.normal;

      pendingNormalAccessors.push(pendingAccessor);
    }
  }

  return Promise.all([
    Promise.all(pendingPositionAccessors),
    Promise.all(pendingNormalAccessors),
  ]).then((accessors) => {
    const morphPositions = accessors[0];
    const morphNormals = accessors[1];

    // Clone morph target accessors before modifying them.

    for (var i = 0, il = morphPositions.length; i < il; i++) {
      if (geometry.attributes.position === morphPositions[i]) continue;

      morphPositions[i] = cloneBufferAttribute(morphPositions[i]);
    }

    for (var i = 0, il = morphNormals.length; i < il; i++) {
      if (geometry.attributes.normal === morphNormals[i]) continue;

      morphNormals[i] = cloneBufferAttribute(morphNormals[i]);
    }

    for (var i = 0, il = targets.length; i < il; i++) {
      const target = targets[i];
      const attributeName = `morphTarget${i}`;

      if (hasMorphPosition) {
        // Three.js morph position is absolute value. The formula is
        //   basePosition
        //     + weight0 * ( morphPosition0 - basePosition )
        //     + weight1 * ( morphPosition1 - basePosition )
        //     ...
        // while the glTF one is relative
        //   basePosition
        //     + weight0 * glTFmorphPosition0
        //     + weight1 * glTFmorphPosition1
        //     ...
        // then we need to convert from relative to absolute here.

        if (target.POSITION !== undefined) {
          const positionAttribute = morphPositions[i];
          positionAttribute.name = attributeName;

          const { position } = geometry.attributes;

          for (var j = 0, jl = positionAttribute.count; j < jl; j++) {
            positionAttribute.setXYZ(
              j,
              positionAttribute.getX(j) + position.getX(j),
              positionAttribute.getY(j) + position.getY(j),
              positionAttribute.getZ(j) + position.getZ(j)
            );
          }
        }
      }

      if (hasMorphNormal) {
        // see target.POSITION's comment

        if (target.NORMAL !== undefined) {
          const normalAttribute = morphNormals[i];
          normalAttribute.name = attributeName;

          const { normal } = geometry.attributes;

          for (var j = 0, jl = normalAttribute.count; j < jl; j++) {
            normalAttribute.setXYZ(
              j,
              normalAttribute.getX(j) + normal.getX(j),
              normalAttribute.getY(j) + normal.getY(j),
              normalAttribute.getZ(j) + normal.getZ(j)
            );
          }
        }
      }
    }

    if (hasMorphPosition) geometry.morphAttributes.position = morphPositions;
    if (hasMorphNormal) geometry.morphAttributes.normal = morphNormals;

    return geometry;
  });
}

/**
 * @param {Mesh} mesh
 * @param {GLTF.Mesh} meshDef
 */
function updateMorphTargets(mesh, meshDef) {
  mesh.updateMorphTargets();

  if (meshDef.weights !== undefined) {
    for (var i = 0, il = meshDef.weights.length; i < il; i++) {
      mesh.morphTargetInfluences[i] = meshDef.weights[i];
    }
  }

  // .extras has user-defined data, so check that .extras.targetNames is an array.
  if (meshDef.extras && Array.isArray(meshDef.extras.targetNames)) {
    const { targetNames } = meshDef.extras;

    if (mesh.morphTargetInfluences.length === targetNames.length) {
      mesh.morphTargetDictionary = {};

      for (var i = 0, il = targetNames.length; i < il; i++) {
        mesh.morphTargetDictionary[targetNames[i]] = i;
      }
    } else {
      console.warn('GLTFLoader: Invalid extras.targetNames length. Ignoring names.');
    }
  }
}
function isObjectEqual(a, b) {
  if (Object.keys(a).length !== Object.keys(b).length) return false;

  for (const key in a) {
    if (a[key] !== b[key]) return false;
  }

  return true;
}

function createPrimitiveKey(primitiveDef) {
  const dracoExtension = primitiveDef.extensions && primitiveDef.extensions[EXTENSIONS.KHR_DRACO_MESH_COMPRESSION];
  let geometryKey;

  if (dracoExtension) {
    geometryKey = `draco:${dracoExtension.bufferView
    }:${dracoExtension.indices
    }:${createAttributesKey(dracoExtension.attributes)}`;
  } else {
    geometryKey = `${primitiveDef.indices}:${createAttributesKey(primitiveDef.attributes)}:${primitiveDef.mode}`;
  }

  return geometryKey;
}

function createAttributesKey(attributes) {
  let attributesKey = '';

  const keys = Object.keys(attributes).sort();

  for (let i = 0, il = keys.length; i < il; i++) {
    attributesKey += `${keys[i]}:${attributes[keys[i]]};`;
  }

  return attributesKey;
}

function cloneBufferAttribute(attribute) {
  if (attribute.isInterleavedBufferAttribute) {
    const { count } = attribute;
    const { itemSize } = attribute;
    const array = attribute.array.slice(0, count * itemSize);

    for (let i = 0, j = 0; i < count; ++i) {
      array[j++] = attribute.getX(i);
      if (itemSize >= 2) array[j++] = attribute.getY(i);
      if (itemSize >= 3) array[j++] = attribute.getZ(i);
      if (itemSize >= 4) array[j++] = attribute.getW(i);
    }

    return new BufferAttribute(array, itemSize, attribute.normalized);
  }

  return attribute.clone();
}

/* GLTF PARSER */

function GLTFParser(json, extensions, options) {
  this.json = json || {};
  this.extensions = extensions || {};
  this.options = options || {};

  // loader object cache
  this.cache = new GLTFRegistry();

  // BufferGeometry caching
  this.primitiveCache = {};

  this.textureLoader = new TextureLoader(this.options.manager);
  this.textureLoader.setCrossOrigin(this.options.crossOrigin);

  this.fileLoader = new FileLoader(this.options.manager);
  this.fileLoader.setResponseType('arraybuffer');
}

GLTFParser.prototype.parse = function (onLoad, onError) {
  const parser = this;
  const { json } = this;
  const { extensions } = this;

  // Clear the loader cache
  this.cache.removeAll();

  // Mark the special nodes/meshes in json for efficient parse
  this.markDefs();

  Promise.all([

    this.getDependencies('scene'),
    this.getDependencies('animation'),
    this.getDependencies('camera'),

  ]).then((dependencies) => {
    const result = {
      scene: dependencies[0][json.scene || 0],
      scenes: dependencies[0],
      animations: dependencies[1],
      cameras: dependencies[2],
      asset: json.asset,
      parser,
      userData: {},
    };

    addUnknownExtensionsToUserData(extensions, result, json);

    onLoad(result);
  }).catch(onError);
};

/**
 * Marks the special nodes/meshes in json for efficient parse.
 */
GLTFParser.prototype.markDefs = function () {
  const nodeDefs = this.json.nodes || [];
  const skinDefs = this.json.skins || [];
  const meshDefs = this.json.meshes || [];

  const meshReferences = {};
  const meshUses = {};

  // Nothing in the node definition indicates whether it is a Bone or an
  // Object3D. Use the skins' joint references to mark bones.
  for (let skinIndex = 0, skinLength = skinDefs.length; skinIndex < skinLength; skinIndex++) {
    const { joints } = skinDefs[skinIndex];

    for (let i = 0, il = joints.length; i < il; i++) {
      nodeDefs[joints[i]].isBone = true;
    }
  }

  // Meshes can (and should) be reused by multiple nodes in a glTF asset. To
  // avoid having more than one Mesh with the same name, count
  // references and rename instances below.
  //
  // Example: CesiumMilkTruck sample model reuses "Wheel" meshes.
  for (let nodeIndex = 0, nodeLength = nodeDefs.length; nodeIndex < nodeLength; nodeIndex++) {
    const nodeDef = nodeDefs[nodeIndex];

    if (nodeDef.mesh !== undefined) {
      if (meshReferences[nodeDef.mesh] === undefined) {
        meshReferences[nodeDef.mesh] = meshUses[nodeDef.mesh] = 0;
      }

      meshReferences[nodeDef.mesh]++;

      // Nothing in the mesh definition indicates whether it is
      // a SkinnedMesh or Mesh. Use the node's mesh reference
      // to mark SkinnedMesh if node has skin.
      if (nodeDef.skin !== undefined) {
        meshDefs[nodeDef.mesh].isSkinnedMesh = true;
      }
    }
  }

  this.json.meshReferences = meshReferences;
  this.json.meshUses = meshUses;
};

/**
 * Requests the specified dependency asynchronously, with caching.
 * @param {string} type
 * @param {number} index
 * @return {Promise<Object3D|Material|Texture|AnimationClip|ArrayBuffer|Object>}
 */
GLTFParser.prototype.getDependency = function (type, index) {
  const cacheKey = `${type}:${index}`;
  let dependency = this.cache.get(cacheKey);

  if (!dependency) {
    switch (type) {
    case 'scene':
      dependency = this.loadScene(index);
      break;

    case 'node':
      dependency = this.loadNode(index);
      break;

    case 'mesh':
      dependency = this.loadMesh(index);
      break;

    case 'accessor':
      dependency = this.loadAccessor(index);
      break;

    case 'bufferView':
      dependency = this.loadBufferView(index);
      break;

    case 'buffer':
      dependency = this.loadBuffer(index);
      break;

    case 'material':
      dependency = this.loadMaterial(index);
      break;

    case 'texture':
      dependency = this.loadTexture(index);
      break;

    case 'skin':
      dependency = this.loadSkin(index);
      break;

    case 'animation':
      dependency = this.loadAnimation(index);
      break;

    case 'camera':
      dependency = this.loadCamera(index);
      break;

    case 'light':
      dependency = this.extensions[EXTENSIONS.KHR_LIGHTS_PUNCTUAL].loadLight(index);
      break;

    default:
      throw new Error(`Unknown type: ${type}`);
    }

    this.cache.add(cacheKey, dependency);
  }

  return dependency;
};

/**
 * Requests all dependencies of the specified type asynchronously, with caching.
 * @param {string} type
 * @return {Promise<Array<Object>>}
 */
GLTFParser.prototype.getDependencies = function (type) {
  let dependencies = this.cache.get(type);

  if (!dependencies) {
    const parser = this;
    const defs = this.json[type + (type === 'mesh' ? 'es' : 's')] || [];

    dependencies = Promise.all(defs.map((def, index) => parser.getDependency(type, index)));

    this.cache.add(type, dependencies);
  }

  return dependencies;
};

/**
 * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#buffers-and-buffer-views
 * @param {number} bufferIndex
 * @return {Promise<ArrayBuffer>}
 */
GLTFParser.prototype.loadBuffer = function (bufferIndex) {
  const bufferDef = this.json.buffers[bufferIndex];
  const loader = this.fileLoader;

  if (bufferDef.type && bufferDef.type !== 'arraybuffer') {
    throw new Error(`GLTFLoader: ${bufferDef.type} buffer type is not supported.`);
  }

  // If present, GLB container is required to be the first buffer.
  if (bufferDef.uri === undefined && bufferIndex === 0) {
    return Promise.resolve(this.extensions[EXTENSIONS.KHR_BINARY_GLTF].body);
  }

  const { options } = this;

  return new Promise(((resolve, reject) => {
    loader.load(resolveURL(bufferDef.uri, options.path), resolve, undefined, () => {
      reject(new Error(`GLTFLoader: Failed to load buffer "${bufferDef.uri}".`));
    });
  }));
};

/**
 * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#buffers-and-buffer-views
 * @param {number} bufferViewIndex
 * @return {Promise<ArrayBuffer>}
 */
GLTFParser.prototype.loadBufferView = function (bufferViewIndex) {
  const bufferViewDef = this.json.bufferViews[bufferViewIndex];

  return this.getDependency('buffer', bufferViewDef.buffer).then((buffer) => {
    const byteLength = bufferViewDef.byteLength || 0;
    const byteOffset = bufferViewDef.byteOffset || 0;
    return buffer.slice(byteOffset, byteOffset + byteLength);
  });
};

/**
 * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#accessors
 * @param {number} accessorIndex
 * @return {Promise<BufferAttribute|InterleavedBufferAttribute>}
 */
GLTFParser.prototype.loadAccessor = function (accessorIndex) {
  const parser = this;
  const { json } = this;

  const accessorDef = this.json.accessors[accessorIndex];

  if (accessorDef.bufferView === undefined && accessorDef.sparse === undefined) {
    // Ignore empty accessors, which may be used to declare runtime
    // information about attributes coming from another source (e.g. Draco
    // compression extension).
    return Promise.resolve(null);
  }

  const pendingBufferViews = [];

  if (accessorDef.bufferView !== undefined) {
    pendingBufferViews.push(this.getDependency('bufferView', accessorDef.bufferView));
  } else {
    pendingBufferViews.push(null);
  }

  if (accessorDef.sparse !== undefined) {
    pendingBufferViews.push(this.getDependency('bufferView', accessorDef.sparse.indices.bufferView));
    pendingBufferViews.push(this.getDependency('bufferView', accessorDef.sparse.values.bufferView));
  }

  return Promise.all(pendingBufferViews).then((bufferViews) => {
    const bufferView = bufferViews[0];

    const itemSize = WEBGL_TYPE_SIZES[accessorDef.type];
    const TypedArray = WEBGL_COMPONENT_TYPES[accessorDef.componentType];

    // For VEC3: itemSize is 3, elementBytes is 4, itemBytes is 12.
    const elementBytes = TypedArray.BYTES_PER_ELEMENT;
    const itemBytes = elementBytes * itemSize;
    const byteOffset = accessorDef.byteOffset || 0;
    const byteStride = accessorDef.bufferView !== undefined ? json.bufferViews[accessorDef.bufferView].byteStride : undefined;
    const normalized = accessorDef.normalized === true;
    let array; let
      bufferAttribute;

    // The buffer is not interleaved if the stride is the item size in bytes.
    if (byteStride && byteStride !== itemBytes) {
      const ibCacheKey = `InterleavedBuffer:${accessorDef.bufferView}:${accessorDef.componentType}`;
      let ib = parser.cache.get(ibCacheKey);

      if (!ib) {
        // Use the full buffer if it's interleaved.
        array = new TypedArray(bufferView);

        // Integer parameters to IB/IBA are in array elements, not bytes.
        ib = new InterleavedBuffer(array, byteStride / elementBytes);

        parser.cache.add(ibCacheKey, ib);
      }

      bufferAttribute = new InterleavedBufferAttribute(ib, itemSize, byteOffset / elementBytes, normalized);
    } else {
      if (bufferView === null) {
        array = new TypedArray(accessorDef.count * itemSize);
      } else {
        array = new TypedArray(bufferView, byteOffset, accessorDef.count * itemSize);
      }

      bufferAttribute = new BufferAttribute(array, itemSize, normalized);
    }

    // https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#sparse-accessors
    if (accessorDef.sparse !== undefined) {
      const itemSizeIndices = WEBGL_TYPE_SIZES.SCALAR;
      const TypedArrayIndices = WEBGL_COMPONENT_TYPES[accessorDef.sparse.indices.componentType];

      const byteOffsetIndices = accessorDef.sparse.indices.byteOffset || 0;
      const byteOffsetValues = accessorDef.sparse.values.byteOffset || 0;

      const sparseIndices = new TypedArrayIndices(bufferViews[1], byteOffsetIndices, accessorDef.sparse.count * itemSizeIndices);
      const sparseValues = new TypedArray(bufferViews[2], byteOffsetValues, accessorDef.sparse.count * itemSize);

      if (bufferView !== null) {
        // Avoid modifying the original ArrayBuffer, if the bufferView wasn't initialized with zeroes.
        bufferAttribute.setArray(bufferAttribute.array.slice());
      }

      for (let i = 0, il = sparseIndices.length; i < il; i++) {
        const index = sparseIndices[i];

        bufferAttribute.setX(index, sparseValues[i * itemSize]);
        if (itemSize >= 2) bufferAttribute.setY(index, sparseValues[i * itemSize + 1]);
        if (itemSize >= 3) bufferAttribute.setZ(index, sparseValues[i * itemSize + 2]);
        if (itemSize >= 4) bufferAttribute.setW(index, sparseValues[i * itemSize + 3]);
        if (itemSize >= 5) throw new Error('GLTFLoader: Unsupported itemSize in sparse BufferAttribute.');
      }
    }

    return bufferAttribute;
  });
};

/**
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#textures
 * @param {number} textureIndex
 * @return {Promise<Texture>}
 */
GLTFParser.prototype.loadTexture = function (textureIndex) {
  const parser = this;
  const { json } = this;
  const { options } = this;
  const { textureLoader } = this;

  const URL = window.URL || window.webkitURL;

  const textureDef = json.textures[textureIndex];

  const textureExtensions = textureDef.extensions || {};

  let source;

  if (textureExtensions[EXTENSIONS.MSFT_TEXTURE_DDS]) {
    source = json.images[textureExtensions[EXTENSIONS.MSFT_TEXTURE_DDS].source];
  } else {
    source = json.images[textureDef.source];
  }

  let sourceURI = source.uri;
  let isObjectURL = false;

  if (source.bufferView !== undefined) {
    // Load binary image data from bufferView, if provided.

    sourceURI = parser.getDependency('bufferView', source.bufferView).then((bufferView) => {
      isObjectURL = true;
      const blob = new Blob([bufferView], { type: source.mimeType });
      sourceURI = URL.createObjectURL(blob);
      return sourceURI;
    });
  }

  return Promise.resolve(sourceURI).then((sourceURI) => {
    // Load Texture resource.

    let loader = Loader.Handlers.get(sourceURI);

    if (!loader) {
      loader = textureExtensions[EXTENSIONS.MSFT_TEXTURE_DDS]
        ? parser.extensions[EXTENSIONS.MSFT_TEXTURE_DDS].ddsLoader
        : textureLoader;
    }

    return new Promise(((resolve, reject) => {
      loader.load(resolveURL(sourceURI, options.path), resolve, undefined, reject);
    }));
  }).then((texture) => {
    // Clean up resources and configure Texture.

    if (isObjectURL === true) {
      URL.revokeObjectURL(sourceURI);
    }

    texture.flipY = false;

    if (textureDef.name !== undefined) texture.name = textureDef.name;

    // Ignore unknown mime types, like DDS files.
    if (source.mimeType in MIME_TYPE_FORMATS) {
      texture.format = MIME_TYPE_FORMATS[source.mimeType];
    }

    const samplers = json.samplers || {};
    const sampler = samplers[textureDef.sampler] || {};

    texture.magFilter = WEBGL_FILTERS[sampler.magFilter] || LinearFilter;
    texture.minFilter = WEBGL_FILTERS[sampler.minFilter] || LinearMipMapLinearFilter;
    texture.wrapS = WEBGL_WRAPPINGS[sampler.wrapS] || RepeatWrapping;
    texture.wrapT = WEBGL_WRAPPINGS[sampler.wrapT] || RepeatWrapping;

    return texture;
  });
};

/**
 * Asynchronously assigns a texture to the given material parameters.
 * @param {Object} materialParams
 * @param {string} mapName
 * @param {Object} mapDef
 * @return {Promise}
 */
GLTFParser.prototype.assignTexture = function (materialParams, mapName, mapDef) {
  const parser = this;

  return this.getDependency('texture', mapDef.index).then((texture) => {
    switch (mapName) {
    case 'aoMap':
    case 'emissiveMap':
    case 'metalnessMap':
    case 'normalMap':
    case 'roughnessMap':
      texture.format = RGBFormat;
      break;
    }

    if (parser.extensions[EXTENSIONS.KHR_TEXTURE_TRANSFORM]) {
      const transform = mapDef.extensions !== undefined ? mapDef.extensions[EXTENSIONS.KHR_TEXTURE_TRANSFORM] : undefined;

      if (transform) {
        texture = parser.extensions[EXTENSIONS.KHR_TEXTURE_TRANSFORM].extendTexture(texture, transform);
      }
    }

    materialParams[mapName] = texture;
  });
};

/**
 * Assigns final material to a Mesh, Line, or Points instance. The instance
 * already has a material (generated from the glTF material options alone)
 * but reuse of the same glTF material may require multiple threejs materials
 * to accomodate different primitive types, defines, etc. New materials will
 * be created if necessary, and reused from a cache.
 * @param  {Object3D} mesh Mesh, Line, or Points instance.
 */
GLTFParser.prototype.assignFinalMaterial = function (mesh) {
  const { geometry } = mesh;
  let { material } = mesh;
  const { extensions } = this;

  const useVertexTangents = geometry.attributes.tangent !== undefined;
  const useVertexColors = geometry.attributes.color !== undefined;
  const useFlatShading = geometry.attributes.normal === undefined;
  const useSkinning = mesh.isSkinnedMesh === true;
  const useMorphTargets = Object.keys(geometry.morphAttributes).length > 0;
  const useMorphNormals = useMorphTargets && geometry.morphAttributes.normal !== undefined;

  if (mesh.isPoints) {
    var cacheKey = `PointsMaterial:${material.uuid}`;

    let pointsMaterial = this.cache.get(cacheKey);

    if (!pointsMaterial) {
      pointsMaterial = new PointsMaterial();
      Material.prototype.copy.call(pointsMaterial, material);
      pointsMaterial.color.copy(material.color);
      pointsMaterial.map = material.map;
      pointsMaterial.lights = false; // PointsMaterial doesn't support lights yet

      this.cache.add(cacheKey, pointsMaterial);
    }

    material = pointsMaterial;
  } else if (mesh.isLine) {
    var cacheKey = `LineBasicMaterial:${material.uuid}`;

    let lineMaterial = this.cache.get(cacheKey);

    if (!lineMaterial) {
      lineMaterial = new LineBasicMaterial();
      Material.prototype.copy.call(lineMaterial, material);
      lineMaterial.color.copy(material.color);
      lineMaterial.lights = false; // LineBasicMaterial doesn't support lights yet

      this.cache.add(cacheKey, lineMaterial);
    }

    material = lineMaterial;
  }

  // Clone the material if it will be modified
  if (useVertexTangents || useVertexColors || useFlatShading || useSkinning || useMorphTargets) {
    var cacheKey = `ClonedMaterial:${material.uuid}:`;

    if (material.isGLTFSpecularGlossinessMaterial) cacheKey += 'specular-glossiness:';
    if (useSkinning) cacheKey += 'skinning:';
    if (useVertexTangents) cacheKey += 'vertex-tangents:';
    if (useVertexColors) cacheKey += 'vertex-colors:';
    if (useFlatShading) cacheKey += 'flat-shading:';
    if (useMorphTargets) cacheKey += 'morph-targets:';
    if (useMorphNormals) cacheKey += 'morph-normals:';

    let cachedMaterial = this.cache.get(cacheKey);

    if (!cachedMaterial) {
      cachedMaterial = material.isGLTFSpecularGlossinessMaterial
        ? extensions[EXTENSIONS.KHR_MATERIALS_PBR_SPECULAR_GLOSSINESS].cloneMaterial(material)
        : material.clone();

      if (useSkinning) cachedMaterial.skinning = true;
      if (useVertexTangents) cachedMaterial.vertexTangents = true;
      if (useVertexColors) cachedMaterial.vertexColors = VertexColors;
      if (useFlatShading) cachedMaterial.flatShading = true;
      if (useMorphTargets) cachedMaterial.morphTargets = true;
      if (useMorphNormals) cachedMaterial.morphNormals = true;

      this.cache.add(cacheKey, cachedMaterial);
    }

    material = cachedMaterial;
  }

  // workarounds for mesh and geometry

  if (material.aoMap && geometry.attributes.uv2 === undefined && geometry.attributes.uv !== undefined) {
    console.log('GLTFLoader: Duplicating UVs to support aoMap.');
    geometry.addAttribute('uv2', new BufferAttribute(geometry.attributes.uv.array, 2));
  }

  if (material.isGLTFSpecularGlossinessMaterial) {
    // for GLTFSpecularGlossinessMaterial(ShaderMaterial) uniforms runtime update
    mesh.onBeforeRender = extensions[EXTENSIONS.KHR_MATERIALS_PBR_SPECULAR_GLOSSINESS].refreshUniforms;
  }

  mesh.material = material;
};

/**
 * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#materials
 * @param {number} materialIndex
 * @return {Promise<Material>}
 */
GLTFParser.prototype.loadMaterial = function (materialIndex) {
  const parser = this;
  const { json } = this;
  const { extensions } = this;
  const materialDef = json.materials[materialIndex];

  let materialType;
  const materialParams = {};
  const materialExtensions = materialDef.extensions || {};

  const pending = [];

  if (materialExtensions[EXTENSIONS.KHR_MATERIALS_PBR_SPECULAR_GLOSSINESS]) {
    const sgExtension = extensions[EXTENSIONS.KHR_MATERIALS_PBR_SPECULAR_GLOSSINESS];
    materialType = sgExtension.getMaterialType(materialDef);
    pending.push(sgExtension.extendParams(materialParams, materialDef, parser));
  } else if (materialExtensions[EXTENSIONS.KHR_MATERIALS_UNLIT]) {
    const kmuExtension = extensions[EXTENSIONS.KHR_MATERIALS_UNLIT];
    materialType = kmuExtension.getMaterialType(materialDef);
    pending.push(kmuExtension.extendParams(materialParams, materialDef, parser));
  } else {
    // Specification:
    // https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#metallic-roughness-material

    materialType = MeshStandardMaterial;

    const metallicRoughness = materialDef.pbrMetallicRoughness || {};

    materialParams.color = new Color(1.0, 1.0, 1.0);
    materialParams.opacity = 1.0;

    if (Array.isArray(metallicRoughness.baseColorFactor)) {
      const array = metallicRoughness.baseColorFactor;

      materialParams.color.fromArray(array);
      materialParams.opacity = array[3];
    }

    if (metallicRoughness.baseColorTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'map', metallicRoughness.baseColorTexture));
    }

    materialParams.metalness = metallicRoughness.metallicFactor !== undefined ? metallicRoughness.metallicFactor : 1.0;
    materialParams.roughness = metallicRoughness.roughnessFactor !== undefined ? metallicRoughness.roughnessFactor : 1.0;

    if (metallicRoughness.metallicRoughnessTexture !== undefined) {
      pending.push(parser.assignTexture(materialParams, 'metalnessMap', metallicRoughness.metallicRoughnessTexture));
      pending.push(parser.assignTexture(materialParams, 'roughnessMap', metallicRoughness.metallicRoughnessTexture));
    }
  }

  if (materialDef.doubleSided === true) {
    materialParams.side = DoubleSide;
  }

  const alphaMode = materialDef.alphaMode || ALPHA_MODES.OPAQUE;

  if (alphaMode === ALPHA_MODES.BLEND) {
    materialParams.transparent = true;
  } else {
    materialParams.transparent = false;

    if (alphaMode === ALPHA_MODES.MASK) {
      materialParams.alphaTest = materialDef.alphaCutoff !== undefined ? materialDef.alphaCutoff : 0.5;
    }
  }

  if (materialDef.normalTexture !== undefined && materialType !== MeshBasicMaterial) {
    pending.push(parser.assignTexture(materialParams, 'normalMap', materialDef.normalTexture));

    materialParams.normalScale = new Vector2(1, 1);

    if (materialDef.normalTexture.scale !== undefined) {
      materialParams.normalScale.set(materialDef.normalTexture.scale, materialDef.normalTexture.scale);
    }
  }

  if (materialDef.occlusionTexture !== undefined && materialType !== MeshBasicMaterial) {
    pending.push(parser.assignTexture(materialParams, 'aoMap', materialDef.occlusionTexture));

    if (materialDef.occlusionTexture.strength !== undefined) {
      materialParams.aoMapIntensity = materialDef.occlusionTexture.strength;
    }
  }

  if (materialDef.emissiveFactor !== undefined && materialType !== MeshBasicMaterial) {
    materialParams.emissive = new Color().fromArray(materialDef.emissiveFactor);
  }

  if (materialDef.emissiveTexture !== undefined && materialType !== MeshBasicMaterial) {
    pending.push(parser.assignTexture(materialParams, 'emissiveMap', materialDef.emissiveTexture));
  }

  return Promise.all(pending).then(() => {
    let material;

    if (materialType === ShaderMaterial) {
      material = extensions[EXTENSIONS.KHR_MATERIALS_PBR_SPECULAR_GLOSSINESS].createMaterial(materialParams);
    } else {
      material = new materialType(materialParams);
    }

    if (materialDef.name !== undefined) material.name = materialDef.name;

    // baseColorTexture, emissiveTexture, and specularGlossinessTexture use sRGB encoding.
    if (material.map) material.map.encoding = sRGBEncoding;
    if (material.emissiveMap) material.emissiveMap.encoding = sRGBEncoding;
    if (material.specularMap) material.specularMap.encoding = sRGBEncoding;

    assignExtrasToUserData(material, materialDef);

    if (materialDef.extensions) addUnknownExtensionsToUserData(extensions, material, materialDef);

    return material;
  });
};

/**
 * @param {BufferGeometry} geometry
 * @param {GLTF.Primitive} primitiveDef
 * @param {GLTFParser} parser
 * @return {Promise<BufferGeometry>}
 */
function addPrimitiveAttributes(geometry, primitiveDef, parser) {
  const { attributes } = primitiveDef;

  const pending = [];

  function assignAttributeAccessor(accessorIndex, attributeName) {
    return parser.getDependency('accessor', accessorIndex)
      .then((accessor) => {
        geometry.addAttribute(attributeName, accessor);
      });
  }

  for (const gltfAttributeName in attributes) {
    const threeAttributeName = ATTRIBUTES[gltfAttributeName] || gltfAttributeName.toLowerCase();

    // Skip attributes already provided by e.g. Draco extension.
    if (threeAttributeName in geometry.attributes) continue;

    pending.push(assignAttributeAccessor(attributes[gltfAttributeName], threeAttributeName));
  }

  if (primitiveDef.indices !== undefined && !geometry.index) {
    const accessor = parser.getDependency('accessor', primitiveDef.indices).then((accessor) => {
      geometry.setIndex(accessor);
    });

    pending.push(accessor);
  }

  assignExtrasToUserData(geometry, primitiveDef);

  return Promise.all(pending).then(() => (primitiveDef.targets !== undefined
    ? addMorphTargets(geometry, primitiveDef.targets, parser)
    : geometry));
}

/**
 * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#geometry
 *
 * Creates BufferGeometries from primitives.
 *
 * @param {Array<GLTF.Primitive>} primitives
 * @return {Promise<Array<BufferGeometry>>}
 */
GLTFParser.prototype.loadGeometries = function (primitives) {
  const parser = this;
  const { extensions } = this;
  const cache = this.primitiveCache;

  function createDracoPrimitive(primitive) {
    return extensions[EXTENSIONS.KHR_DRACO_MESH_COMPRESSION]
      .decodePrimitive(primitive, parser)
      .then(geometry => addPrimitiveAttributes(geometry, primitive, parser));
  }

  const pending = [];

  for (let i = 0, il = primitives.length; i < il; i++) {
    const primitive = primitives[i];
    const cacheKey = createPrimitiveKey(primitive);

    // See if we've already created this geometry
    const cached = cache[cacheKey];

    if (cached) {
      // Use the cached geometry if it exists
      pending.push(cached.promise);
    } else {
      var geometryPromise;

      if (primitive.extensions && primitive.extensions[EXTENSIONS.KHR_DRACO_MESH_COMPRESSION]) {
        // Use DRACO geometry if available
        geometryPromise = createDracoPrimitive(primitive);
      } else {
        // Otherwise create a new geometry
        geometryPromise = addPrimitiveAttributes(new BufferGeometry(), primitive, parser);
      }

      // Cache this geometry
      cache[cacheKey] = { primitive, promise: geometryPromise };

      pending.push(geometryPromise);
    }
  }

  return Promise.all(pending);
};

/**
 * Specification: https://github.com/KhronosGroup/glTF/blob/master/specification/2.0/README.md#meshes
 * @param {number} meshIndex
 * @return {Promise<Group|Mesh|SkinnedMesh>}
 */
GLTFParser.prototype.loadMesh = function (meshIndex) {
  const parser = this;
  const { json } = this;
  const { extensions } = this;

  const meshDef = json.meshes[meshIndex];
  const { primitives } = meshDef;

  const pending = [];

  for (let i = 0, il = primitives.length; i < il; i++) {
    const material = primitives[i].material === undefined
      ? createDefaultMaterial()
      : this.getDependency('material', primitives[i].material);

    pending.push(material);
  }

  return Promise.all(pending).then(originalMaterials => parser.loadGeometries(primitives).then((geometries) => {
    const meshes = [];

    for (var i = 0, il = geometries.length; i < il; i++) {
      const geometry = geometries[i];
      const primitive = primitives[i];

      // 1. create Mesh

      var mesh;

      const material = originalMaterials[i];

      if (primitive.mode === WEBGL_CONSTANTS.TRIANGLES
                    || primitive.mode === WEBGL_CONSTANTS.TRIANGLE_STRIP
                    || primitive.mode === WEBGL_CONSTANTS.TRIANGLE_FAN
                    || primitive.mode === undefined) {
        // .isSkinnedMesh isn't in glTF spec. See .markDefs()
        mesh = meshDef.isSkinnedMesh === true
          ? new SkinnedMesh(geometry, material)
          : new Mesh(geometry, material);

        if (mesh.isSkinnedMesh === true) mesh.normalizeSkinWeights(); // #15319

        if (primitive.mode === WEBGL_CONSTANTS.TRIANGLE_STRIP) {
          mesh.drawMode = TriangleStripDrawMode;
        } else if (primitive.mode === WEBGL_CONSTANTS.TRIANGLE_FAN) {
          mesh.drawMode = TriangleFanDrawMode;
        }
      } else if (primitive.mode === WEBGL_CONSTANTS.LINES) {
        mesh = new LineSegments(geometry, material);
      } else if (primitive.mode === WEBGL_CONSTANTS.LINE_STRIP) {
        mesh = new Line(geometry, material);
      } else if (primitive.mode === WEBGL_CONSTANTS.LINE_LOOP) {
        mesh = new LineLoop(geometry, material);
      } else if (primitive.mode === WEBGL_CONSTANTS.POINTS) {
        mesh = new Points(geometry, material);
      } else {
        throw new Error(`GLTFLoader: Primitive mode unsupported: ${primitive.mode}`);
      }

      if (Object.keys(mesh.geometry.morphAttributes).length > 0) {
        updateMorphTargets(mesh, meshDef);
      }

      mesh.name = meshDef.name || (`mesh_${meshIndex}`);

      if (geometries.length > 1) mesh.name += `_${i}`;

      assignExtrasToUserData(mesh, meshDef);

      parser.assignFinalMaterial(mesh);

      meshes.push(mesh);
    }

    if (meshes.length === 1) {
      return meshes[0];
    }

    const group = new Group();

    for (var i = 0, il = meshes.length; i < il; i++) {
      group.add(meshes[i]);
    }

    return group;
  }));
};

/**
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#cameras
 * @param {number} cameraIndex
 * @return {Promise<Camera>}
 */
GLTFParser.prototype.loadCamera = function (cameraIndex) {
  let camera;
  const cameraDef = this.json.cameras[cameraIndex];
  const params = cameraDef[cameraDef.type];

  if (!params) {
    console.warn('GLTFLoader: Missing camera parameters.');
    return;
  }

  if (cameraDef.type === 'perspective') {
    camera = new PerspectiveCamera(Math.radToDeg(params.yfov), params.aspectRatio || 1, params.znear || 1, params.zfar || 2e6);
  } else if (cameraDef.type === 'orthographic') {
    camera = new OrthographicCamera(params.xmag / -2, params.xmag / 2, params.ymag / 2, params.ymag / -2, params.znear, params.zfar);
  }

  if (cameraDef.name !== undefined) camera.name = cameraDef.name;

  assignExtrasToUserData(camera, cameraDef);

  return Promise.resolve(camera);
};

/**
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#skins
 * @param {number} skinIndex
 * @return {Promise<Object>}
 */
GLTFParser.prototype.loadSkin = function (skinIndex) {
  const skinDef = this.json.skins[skinIndex];

  const skinEntry = { joints: skinDef.joints };

  if (skinDef.inverseBindMatrices === undefined) {
    return Promise.resolve(skinEntry);
  }

  return this.getDependency('accessor', skinDef.inverseBindMatrices).then((accessor) => {
    skinEntry.inverseBindMatrices = accessor;

    return skinEntry;
  });
};

/**
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#animations
 * @param {number} animationIndex
 * @return {Promise<AnimationClip>}
 */
GLTFParser.prototype.loadAnimation = function (animationIndex) {
  const { json } = this;

  const animationDef = json.animations[animationIndex];

  const pendingNodes = [];
  const pendingInputAccessors = [];
  const pendingOutputAccessors = [];
  const pendingSamplers = [];
  const pendingTargets = [];

  for (let i = 0, il = animationDef.channels.length; i < il; i++) {
    const channel = animationDef.channels[i];
    const sampler = animationDef.samplers[channel.sampler];
    const { target } = channel;
    const name = target.node !== undefined ? target.node : target.id; // NOTE: target.id is deprecated.
    const input = animationDef.parameters !== undefined ? animationDef.parameters[sampler.input] : sampler.input;
    const output = animationDef.parameters !== undefined ? animationDef.parameters[sampler.output] : sampler.output;

    pendingNodes.push(this.getDependency('node', name));
    pendingInputAccessors.push(this.getDependency('accessor', input));
    pendingOutputAccessors.push(this.getDependency('accessor', output));
    pendingSamplers.push(sampler);
    pendingTargets.push(target);
  }

  return Promise.all([

    Promise.all(pendingNodes),
    Promise.all(pendingInputAccessors),
    Promise.all(pendingOutputAccessors),
    Promise.all(pendingSamplers),
    Promise.all(pendingTargets),

  ]).then((dependencies) => {
    const nodes = dependencies[0];
    const inputAccessors = dependencies[1];
    const outputAccessors = dependencies[2];
    const samplers = dependencies[3];
    const targets = dependencies[4];

    const tracks = [];

    for (let i = 0, il = nodes.length; i < il; i++) {
      const node = nodes[i];
      const inputAccessor = inputAccessors[i];
      const outputAccessor = outputAccessors[i];
      const sampler = samplers[i];
      const target = targets[i];

      if (node === undefined) continue;

      node.updateMatrix();
      node.matrixAutoUpdate = true;

      var TypedKeyframeTrack;

      switch (PATH_PROPERTIES[target.path]) {
      case PATH_PROPERTIES.weights:

        TypedKeyframeTrack = NumberKeyframeTrack;
        break;

      case PATH_PROPERTIES.rotation:

        TypedKeyframeTrack = QuaternionKeyframeTrack;
        break;

      case PATH_PROPERTIES.position:
      case PATH_PROPERTIES.scale:
      default:

        TypedKeyframeTrack = VectorKeyframeTrack;
        break;
      }

      const targetName = node.name ? node.name : node.uuid;

      const interpolation = sampler.interpolation !== undefined ? INTERPOLATION[sampler.interpolation] : InterpolateLinear;

      var targetNames = [];

      if (PATH_PROPERTIES[target.path] === PATH_PROPERTIES.weights) {
        // Node may be a Group (glTF mesh with several primitives) or a Mesh.
        node.traverse((object) => {
          if (object.isMesh === true && object.morphTargetInfluences) {
            targetNames.push(object.name ? object.name : object.uuid);
          }
        });
      } else {
        targetNames.push(targetName);
      }

      for (let j = 0, jl = targetNames.length; j < jl; j++) {
        const track = new TypedKeyframeTrack(
          `${targetNames[j]}.${PATH_PROPERTIES[target.path]}`,
          inputAccessor.array,
          outputAccessor.array,
          interpolation
        );

        // Override interpolation with custom factory method.
        if (sampler.interpolation === 'CUBICSPLINE') {
          track.createInterpolant = function InterpolantFactoryMethodGLTFCubicSpline(result) {
            // A CUBICSPLINE keyframe in glTF has three output values for each input value,
            // representing inTangent, splineVertex, and outTangent. As a result, track.getValueSize()
            // must be divided by three to get the interpolant's sampleSize argument.

            return new GLTFCubicSplineInterpolant(this.times, this.values, this.getValueSize() / 3, result);
          };

          // Mark as CUBICSPLINE. `track.getInterpolation()` doesn't support custom interpolants.
          track.createInterpolant.isInterpolantFactoryMethodGLTFCubicSpline = true;
        }

        tracks.push(track);
      }
    }

    const name = animationDef.name !== undefined ? animationDef.name : `animation_${animationIndex}`;

    return new AnimationClip(name, undefined, tracks);
  });
};

/**
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#nodes-and-hierarchy
 * @param {number} nodeIndex
 * @return {Promise<Object3D>}
 */
GLTFParser.prototype.loadNode = function (nodeIndex) {
  const { json } = this;
  const { extensions } = this;
  const parser = this;

  const { meshReferences } = json;
  const { meshUses } = json;

  const nodeDef = json.nodes[nodeIndex];

  return (function () {
    // .isBone isn't in glTF spec. See .markDefs
    if (nodeDef.isBone === true) {
      return Promise.resolve(new Bone());
    } if (nodeDef.mesh !== undefined) {
      return parser.getDependency('mesh', nodeDef.mesh).then((mesh) => {
        let node;

        if (meshReferences[nodeDef.mesh] > 1) {
          const instanceNum = meshUses[nodeDef.mesh]++;

          node = mesh.clone();
          node.name += `_instance_${instanceNum}`;

          // onBeforeRender copy for Specular-Glossiness
          node.onBeforeRender = mesh.onBeforeRender;

          for (let i = 0, il = node.children.length; i < il; i++) {
            node.children[i].name += `_instance_${instanceNum}`;
            node.children[i].onBeforeRender = mesh.children[i].onBeforeRender;
          }
        } else {
          node = mesh;
        }

        // if weights are provided on the node, override weights on the mesh.
        if (nodeDef.weights !== undefined) {
          node.traverse((o) => {
            if (!o.isMesh) return;

            for (let i = 0, il = nodeDef.weights.length; i < il; i++) {
              o.morphTargetInfluences[i] = nodeDef.weights[i];
            }
          });
        }

        return node;
      });
    } if (nodeDef.camera !== undefined) {
      return parser.getDependency('camera', nodeDef.camera);
    } if (nodeDef.extensions
            && nodeDef.extensions[EXTENSIONS.KHR_LIGHTS_PUNCTUAL]
            && nodeDef.extensions[EXTENSIONS.KHR_LIGHTS_PUNCTUAL].light !== undefined) {
      return parser.getDependency('light', nodeDef.extensions[EXTENSIONS.KHR_LIGHTS_PUNCTUAL].light);
    }

    return Promise.resolve(new Object3D());
  }()).then((node) => {
    if (nodeDef.name !== undefined) {
      node.name = PropertyBinding.sanitizeNodeName(nodeDef.name);
    }

    assignExtrasToUserData(node, nodeDef);

    if (nodeDef.extensions) addUnknownExtensionsToUserData(extensions, node, nodeDef);

    if (nodeDef.matrix !== undefined) {
      const matrix = new Matrix4();
      matrix.fromArray(nodeDef.matrix);
      node.applyMatrix(matrix);
    } else {
      if (nodeDef.translation !== undefined) {
        node.position.fromArray(nodeDef.translation);
      }

      if (nodeDef.rotation !== undefined) {
        node.quaternion.fromArray(nodeDef.rotation);
      }

      if (nodeDef.scale !== undefined) {
        node.scale.fromArray(nodeDef.scale);
      }
    }

    return node;
  });
};

/**
 * Specification: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#scenes
 * @param {number} sceneIndex
 * @return {Promise<Scene>}
 */
GLTFParser.prototype.loadScene = (function () {
  // scene node hierachy builder

  function buildNodeHierachy(nodeId, parentObject, json, parser) {
    const nodeDef = json.nodes[nodeId];

    return parser.getDependency('node', nodeId).then((node) => {
      if (nodeDef.skin === undefined) return node;

      // build skeleton here as well

      let skinEntry;

      return parser.getDependency('skin', nodeDef.skin).then((skin) => {
        skinEntry = skin;

        const pendingJoints = [];

        for (let i = 0, il = skinEntry.joints.length; i < il; i++) {
          pendingJoints.push(parser.getDependency('node', skinEntry.joints[i]));
        }

        return Promise.all(pendingJoints);
      }).then((jointNodes) => {
        const meshes = node.isGroup === true ? node.children : [node];

        for (let i = 0, il = meshes.length; i < il; i++) {
          const mesh = meshes[i];

          const bones = [];
          const boneInverses = [];

          for (let j = 0, jl = jointNodes.length; j < jl; j++) {
            const jointNode = jointNodes[j];

            if (jointNode) {
              bones.push(jointNode);

              const mat = new Matrix4();

              if (skinEntry.inverseBindMatrices !== undefined) {
                mat.fromArray(skinEntry.inverseBindMatrices.array, j * 16);
              }

              boneInverses.push(mat);
            } else {
              console.warn('GLTFLoader: Joint "%s" could not be found.', skinEntry.joints[j]);
            }
          }

          mesh.bind(new Skeleton(bones, boneInverses), mesh.matrixWorld);
        }

        return node;
      });
    }).then((node) => {
      // build node hierachy

      parentObject.add(node);

      const pending = [];

      if (nodeDef.children) {
        const { children } = nodeDef;

        for (let i = 0, il = children.length; i < il; i++) {
          const child = children[i];
          pending.push(buildNodeHierachy(child, node, json, parser));
        }
      }

      return Promise.all(pending);
    });
  }

  return function loadScene(sceneIndex) {
    const { json } = this;
    const { extensions } = this;
    const sceneDef = this.json.scenes[sceneIndex];
    const parser = this;

    const scene = new Scene();
    if (sceneDef.name !== undefined) scene.name = sceneDef.name;

    assignExtrasToUserData(scene, sceneDef);

    if (sceneDef.extensions) addUnknownExtensionsToUserData(extensions, scene, sceneDef);

    const nodeIds = sceneDef.nodes || [];

    const pending = [];

    for (let i = 0, il = nodeIds.length; i < il; i++) {
      pending.push(buildNodeHierachy(nodeIds[i], scene, json, parser));
    }

    return Promise.all(pending).then(() => scene);
  };
}());

export { GLTFLoader };
